import {
  AttendanceAggregatePeriodResponse,
  AttendanceAggregateTimetableStructureResponse,
  AttendanceEntryForRelationUpdate,
  AttendanceSessionEnum,
  AttendanceSessionFrequency,
  attendanceSessionMap,
  AttendanceTrackingPointsCell,
  AttendanceTrackingPointsWeek,
  CreateEntriesForRelationRequest,
  JustificationBase,
  JustificationStatus,
} from '@schooly/api';
import { AttendanceAggregateRegisterGroupResponse } from 'libs/api/src/lib/generated/schooly-api-v2';

import { takeAttendancePath } from '../../../Groups/TakeAttendanceModal/take-attendance-utils';
import { AttendanceGridSession, AttendanceSidebarFocus } from '../common/types';

export type SidebarEntryGroup = AttendanceAggregateRegisterGroupResponse & {
  period_id: string | null;
};

export type AttendancePeriod = AttendanceAggregatePeriodResponse & {
  timetable_structure: AttendanceAggregateTimetableStructureResponse;
  lesson: AttendanceAggregatePeriodResponse['lesson'] & { name: string; order: number };
};

const isAttendancePeriod = (
  period: AttendanceAggregatePeriodResponse,
): period is AttendancePeriod =>
  period.timetable_structure !== null &&
  period.lesson.name !== null &&
  period.lesson.order !== null;

export type AttendanceFormEntry = AttendanceEntryForRelationUpdate & {
  comment: string | null;
  session: AttendanceSessionEnum | null;
  period_ids: string[];
};

export type AttendanceFormEntries = Omit<CreateEntriesForRelationRequest, 'entries'> & {
  entries: AttendanceFormEntry[];
};

export type SidebarEntry = Omit<
  AttendanceTrackingPointsCell,
  'period_id' | 'register' | 'attendance_entry_id' | 'register_group_list'
> & {
  periods: AttendancePeriod[];
  groups: SidebarEntryGroup[];
  justification: JustificationBase | null;
  absence_request: AttendanceTrackingPointsWeek['rows'][number]['absence_request'];
};

export const buildSidebarEntries = (weeks: AttendanceTrackingPointsWeek[]): SidebarEntry[] => {
  if (!weeks.length) return [];

  const entries = new Map<string, SidebarEntry>();

  for (const week of weeks) {
    for (const row of week.rows) {
      row.cells.forEach((cell, index) => {
        const key = cell.session ?? cell.period_id;
        if (!key) return;

        let entry = entries.get(key);

        if (!entry) {
          entry = {
            session: cell.session,
            attendance_code_id: cell.attendance_code_id,
            comments: cell.comments,
            justification: row.justification,
            absence_request: row.absence_request,
            latest_change: cell.latest_change,
            created_at: cell.created_at,
            updated_at: cell.updated_at,
            periods: [],
            groups: [],
          };
          entries.set(key, entry);
        }

        const column = week.tracking_points[index];
        if (cell.period_id && column?.period && isAttendancePeriod(column.period)) {
          entry.periods.push(column.period);
        }

        entry.groups.push(
          ...cell.register_group_list.map(({ id, name }) => ({
            id,
            name,
            period_id: cell.period_id,
          })),
        );
      });
    }
  }

  return [...entries.values()];
};

export const buildFormEntry = (entry: SidebarEntry, groupId?: string): AttendanceFormEntry => ({
  attendance_code_id: entry.attendance_code_id,
  updated_from_group_id: groupId,
  comment: entry.comments[0]?.text ?? null,
  session: entry.session,
  period_ids: entry.periods.map(({ lesson: { id } }) => id),
});

export const getSessionEntries = (entries: AttendanceFormEntry[]) =>
  entries.reduce<Array<{ session: AttendanceSessionEnum; attendance_code_id: string | null }>>(
    (acc, entry) => {
      if (entry.session) {
        acc.push({ session: entry.session, attendance_code_id: entry.attendance_code_id });
      }
      return acc;
    },
    [],
  );

export const getJustificationWarningStatus = (
  justification: JustificationBase | null,
  resultingJustification: JustificationBase | null,
): JustificationStatus.Pending | JustificationStatus.Closed | null => {
  const status = resultingJustification?.status;

  if (!status || status === justification?.status) return null;
  if (status === JustificationStatus.Pending || status === JustificationStatus.Closed)
    return status;

  return null;
};

export const getTrackingPointLabel = (
  entry: SidebarEntry,
  sessionLabel: string,
  group?: SidebarEntryGroup,
) => {
  const period = entry.periods.find(({ lesson: { id } }) => id === group?.period_id);

  if (!period) return sessionLabel;
  return entry.session ? `${sessionLabel}/${period.lesson.order}` : period.lesson.name;
};

export const getEntryGroupKey = ({ id, period_id }: Pick<SidebarEntryGroup, 'id' | 'period_id'>) =>
  period_id ? `${period_id}-${id}` : id;

type SidebarEntryGroupItem = SidebarEntryGroup & { separator: string };

const sortGroupsByPeriod = (entry: SidebarEntry) => {
  const periodOrder = new Map(entry.periods.map(({ lesson: { id, order } }) => [id, order]));
  const groupOrder = ({ period_id }: SidebarEntryGroup) =>
    period_id ? (periodOrder.get(period_id) ?? 0) : -1;

  return [...entry.groups].sort((a, b) => groupOrder(a) - groupOrder(b));
};

const SAME_POINT_SEPARATOR = ', ';
const NEXT_POINT_SEPARATOR = '/';

const getGroupSeparator = (group: SidebarEntryGroup, next?: SidebarEntryGroup) => {
  if (!next) return '';
  if (group.period_id === next.period_id) return SAME_POINT_SEPARATOR;
  return NEXT_POINT_SEPARATOR;
};

export const getEntryGroups = (entry: SidebarEntry): SidebarEntryGroupItem[] => {
  const groups = entry.periods.length ? sortGroupsByPeriod(entry) : entry.groups;

  return groups.map((group, index) => ({
    ...group,
    separator: getGroupSeparator(group, groups[index + 1]),
  }));
};

export const filterEntryByStructure = (
  entry: SidebarEntry,
  structureId: string,
): SidebarEntry | null => {
  const periods = entry.periods.filter(
    ({ timetable_structure }) => timetable_structure.id === structureId,
  );
  if (!periods.length) return null;

  const periodIds = new Set(periods.map(({ lesson: { id } }) => id));

  return {
    ...entry,
    periods,
    groups: entry.groups.filter((group) => group.period_id && periodIds.has(group.period_id)),
  };
};

export const getSessionEntry = (entries: SidebarEntry[], session: AttendanceSessionFrequency) =>
  entries.find((entry) => entry.session === attendanceSessionMap[session]) ?? null;

export const isPeriodOnlyEntry = (entry: { session: AttendanceSessionEnum | null }) =>
  !entry.session;

export const isPeriodFocus = (focus: AttendanceSidebarFocus): focus is { period_id: string } =>
  'period_id' in focus;

export const isSessionFocused = (focus: AttendanceSidebarFocus, session: AttendanceGridSession) => {
  return !isPeriodFocus(focus) && focus.session === session;
};

export const getFocusedStructureId = (entries: SidebarEntry[], focus: AttendanceSidebarFocus) => {
  if (!isPeriodFocus(focus)) return undefined;

  for (const { periods } of entries) {
    const match = periods.find(({ lesson }) => lesson.id === focus.period_id);
    if (match) return match.timetable_structure.id;
  }

  return undefined;
};

export const isPeriodFocused = (focus: AttendanceSidebarFocus, entry: SidebarEntry): boolean => {
  if (!isPeriodFocus(focus)) return false;
  return entry.periods.some(({ lesson }) => lesson.id === focus.period_id);
};

export const getTakeAttendanceLink = (
  session: AttendanceSessionEnum | null,
  group: SidebarEntryGroup,
  date: string,
) => {
  if (!session && !group.period_id) return undefined;

  return takeAttendancePath({
    groupId: group.id,
    date,
    session,
    periodId: group.period_id,
  });
};

export const getFormEntryIndex = (entries: AttendanceFormEntry[], entry: SidebarEntry) =>
  entries.findIndex((item) => {
    if (entry.session) return item.session === entry.session;

    const periodId = entry.periods[0]?.lesson.id;
    return !item.session && !!periodId && item.period_ids.includes(periodId);
  });

export const getTimetableStructures = (weeks: AttendanceTrackingPointsWeek[]) => {
  if (!weeks.length) return [];

  const structures = new Map<string, AttendanceAggregateTimetableStructureResponse>();

  for (const week of weeks) {
    for (const { period } of week.tracking_points) {
      if (!period?.timetable_structure) continue;

      structures.set(period.timetable_structure.id, period.timetable_structure);
    }
  }

  return [...structures.values()];
};
